import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Truck, CheckCircle, LayoutDashboard, Users, FileCheck, Link2 } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api from "@/utils/api";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/staff/dashboard");
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };const [earnings, setEarnings] = useState(null);
const [earningsRange, setEarningsRange] = useState("today");

useEffect(() => { fetchEarnings(); }, [earningsRange]);

const fetchEarnings = async () => {
  try {
    const today = new Date().toISOString().slice(0, 10);
    let dateFrom = today;
    if (earningsRange === "week") {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      dateFrom = d.toISOString().slice(0, 10);
    } else if (earningsRange === "month") {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      dateFrom = d.toISOString().slice(0, 10);
    }
    const res = await api.get(`/v2/payments/reports/payment-summary?date_from=${dateFrom}&date_to=${today}`);
    setEarnings(res.data.summary);
  } catch { /* ignore */ }
};

  const stats = [
    { label: "Today's Bookings", value: data?.today_bookings ?? 0, icon: Package, color: "text-brand-500", bg: "bg-brand-50", border: "border-l-brand-500" },
    { label: "Total Bookings", value: data?.total_bookings ?? 0, icon: LayoutDashboard, color: "text-blue-500", bg: "bg-blue-50", border: "border-l-blue-500" },
    { label: "Manifested", value: data?.manifested ?? 0, icon: FileCheck, color: "text-indigo-500", bg: "bg-indigo-50", border: "border-l-indigo-500" },
    { label: "Connected", value: data?.connected ?? 0, icon: Link2, color: "text-cyan-500", bg: "bg-cyan-50", border: "border-l-cyan-500" },
    { label: "In Transit", value: data?.in_transit ?? 0, icon: Truck, color: "text-orange-500", bg: "bg-orange-50", border: "border-l-orange-400" },
    { label: "Delivered", value: data?.delivered ?? 0, icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50", border: "border-l-emerald-500" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Staff Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">Welcome, {user?.name}. Here's your daily overview.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`staff-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
              className={`bg-white rounded-lg p-4 border border-slate-200 border-l-4 ${s.border}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider leading-tight">{s.label}</p>
                <div className={`w-7 h-7 ${s.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                  <s.icon className={`w-3.5 h-3.5 ${s.color}`} strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-xl font-heading font-bold text-slate-800">{loading ? "—" : s.value}</p>
            </motion.div>
          ))}
        </div>
{/* Earnings Summary */}
<div className="bg-white rounded-lg border border-slate-200 p-4">
  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
    <h2 className="font-heading font-semibold text-lg text-slate-800 uppercase">Earnings</h2>
    <div className="flex gap-2">
      {[
        { key: "today", label: "Today" },
        { key: "week", label: "This Week" },
        { key: "month", label: "This Month" },
      ].map((r) => (
        <button key={r.key} onClick={() => setEarningsRange(r.key)}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${earningsRange === r.key ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          {r.label}
        </button>
      ))}
    </div>
  </div>
  {earnings ? (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {["CASH", "UPI", "WALLET", "CREDIT"].map((seg) => (
        <div key={seg} className="border border-slate-100 rounded-lg p-3">
          <p className="text-xs font-semibold text-slate-400 uppercase mb-1">{seg}</p>
          <p className="text-lg font-heading font-bold text-emerald-600">₹{earnings[seg]?.paid_total?.toFixed(2) || "0.00"}</p>
          <p className="text-[10px] text-slate-400">{earnings[seg]?.paid_count || 0} paid</p>
          {earnings[seg]?.pending_count > 0 && (
            <p className="text-[10px] text-amber-600 mt-1">₹{earnings[seg]?.pending_total?.toFixed(2)} pending ({earnings[seg]?.pending_count})</p>
          )}
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm text-slate-400">Loading earnings...</p>
  )}
</div>

{/* Recent Shipments */}
        {/* Recent Shipments */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h2 className="font-heading font-semibold text-lg text-slate-800 uppercase">Recent Orders</h2>
            <Link to="/staff/orders" className="text-sm text-brand-500 font-semibold hover:underline">View All →</Link>
          </div>
          {loading ? (
            <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
          ) : data?.recent_shipments?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {["Tracking ID", "AWB", "Mid-mile Partner", "From", "To", "Status"].map(h => (
                      <th key={h} className="px-3 py-2 text-xs font-bold text-slate-400 uppercase text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.recent_shipments.map((s) => (
                    <tr key={s.id} data-testid="staff-order-row" className="hover:bg-slate-50">
                      <td className="px-3 py-2"><span className="font-heading font-bold text-brand-500 text-xs">{s.tracking_id}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-500">{s.awb_number || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 capitalize">{s.midmile_partner || <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.origin?.city}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.destination?.city}</td>
                      <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No bookings yet.</p>
              <Link to="/staff/book" className="text-brand-500 text-sm font-semibold hover:underline mt-2 inline-block">Create your first booking →</Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: "/staff/book", label: "New Booking", icon: Package, desc: "Create a new shipment booking" },
            { to: "/staff/orders", label: "Update AWB / Weight", icon: FileCheck, desc: "Manifest orders with AWB details" },
            { to: "/staff/customers", label: "Manage Customers", icon: Users, desc: "View and add customers" },
          ].map((a) => (
            <Link key={a.to} to={a.to} data-testid={`staff-quick-${a.label.toLowerCase().replace(/ /g, "-")}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-brand-500 hover:shadow-sm transition-all flex items-center gap-4 group">
              <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center group-hover:bg-brand-500 transition-colors">
                <a.icon className="w-5 h-5 text-brand-500 group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-800">{a.label}</p>
                <p className="text-xs text-slate-400">{a.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
